import {Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {map} from "rxjs/operators";
import {ConstantProperty} from "../../../_models/constant-property";
import {Field, FieldMap} from "../../../_typing/query-builder";
import {ModelID} from "../../../_typing/generic-types";
import {ApiService} from "../../../services/api/api.service";
import {SearchQueryOptions} from "../../../services/api/search-query-options";
import {getManyRelationWithIdFilter} from "../../../services/api/filter_utils";
import {
    fieldFromConstantProperty,
    getDirectConstantPropertyId
} from "../query-field.utils";

export interface QueryFieldOption {
    id: string;
    type: 'query_field';
    attributes: {
        name: string;
    };
    field: Field;
}

@Injectable()
export class QueryFieldSearchService {
    private fields: FieldMap;
    private modelTypeId: ModelID;
    private relationshipName: 'component_types' | 'event_types';

    get enabled(): boolean {
        return Boolean(this.modelTypeId && this.relationshipName);
    }

    constructor(private api: ApiService) {
    }

    configure(relationshipName: 'component_types' | 'event_types', modelTypeId: ModelID): void {
        this.relationshipName = relationshipName;
        this.modelTypeId = modelTypeId;
    }

    disable(): void {
        this.relationshipName = null;
        this.modelTypeId = null;
    }

    setFields(fields: FieldMap): void {
        this.fields = fields;
    }

    getSelectedOption(fieldId: string): QueryFieldOption | null {
        const field = this.fields?.[fieldId];
        return field ? this.toOption(fieldId, field) : null;
    }

    addField(option: QueryFieldOption): void {
        if (this.fields && option?.field) {
            this.fields[option.id] = {...option.field, value: option.id};
        }
    }

    fetchData = (pageNumber: number, filterValue: string): Observable<any> => {
        if (!this.enabled) {
            const data = this.getStaticOptions(filterValue);
            return of({data, meta: {count: data.length}});
        }

        const options = new SearchQueryOptions();
        options.page_number = pageNumber;
        options.page_size = 10;
        options.sort = 'name';
        options.filters = [
            getManyRelationWithIdFilter(this.relationshipName, this.modelTypeId),
            {name: 'data_type', op: 'ne', val: 'file'}
        ];

        if (filterValue) {
            options.filters.push({
                or: ['name', 'description', 'alias'].map(name => ({
                    name,
                    op: 'ilike',
                    val: `%${filterValue}%`
                }))
            });
        }

        return this.api.constant_property_light.searchMany(options).pipe(
            map(response => {
                const propertyOptions = response.data
                    .map(property => this.getConstantPropertyOption(property))
                    .filter(Boolean);
                const staticOptions = pageNumber === 1 ?
                    this.getStaticOptions(filterValue)
                        .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name)) :
                    [];
                const data = [...staticOptions, ...propertyOptions];
                return {
                    ...response,
                    data,
                    meta: {
                        ...response.meta,
                        count: (response.meta?.count || 0) + staticOptions.length
                    }
                };
            })
        );
    };

    private getConstantPropertyOption(constantProperty: ConstantProperty): QueryFieldOption | null {
        const fieldId = `[constant_property@${constantProperty.id}]`;
        const field = this.fields?.[fieldId] ||
            fieldFromConstantProperty(constantProperty);
        return field ? this.toOption(fieldId, field) : null;
    }

    private getStaticOptions(filterValue: string): QueryFieldOption[] {
        const normalizedFilter = filterValue?.toLowerCase() || '';
        return Object.entries(this.fields || {})
            .filter(([fieldId]) => !getDirectConstantPropertyId(fieldId))
            .filter(([, field]) => !normalizedFilter || field.name.toLowerCase().includes(normalizedFilter))
            .map(([fieldId, field]) => this.toOption(fieldId, field));
    }

    private toOption(fieldId: string, field: Field): QueryFieldOption {
        return {
            id: fieldId,
            type: 'query_field',
            attributes: {
                name: field.name
            },
            field: {...field, value: fieldId}
        };
    }

}
